import React, { useState, useMemo } from 'react';
import {
	Box,
	IconButton,
	Typography,
	Slide,
	useTheme,
	Button,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
	isSameDay,
	startOfMonth,
	endOfMonth,
	startOfWeek,
	endOfWeek,
	addDays,
} from 'date-fns';

// todo hide non month days
// todo align days to right side 

// Hebrew month names
const hebrewMonths = [
	'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
	'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

// Single-letter Hebrew days, Sunday→Saturday
const hebrewWeekdays = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

interface ShowItem {
	key: number;
	location: string;
	date: string; // e.g. "2025-02-19T20:45:00+02:00"
}

interface CustomCalendarProps {
	shows: ShowItem[];
	selectedDate?: Date | null;
	onDateSelect?: (date: Date) => void;
	minDate?: Date;
	maxDate?: Date;
	/**
	 * If true, hides any week (row) that does NOT contain
	 * at least one clickable (i.e., show) date in range.
	 */
	hideNonClickableRows?: boolean;
	hideCalandar?: () => void;
}

export const CustomCalendar: React.FC<CustomCalendarProps> = ({
	shows,
	selectedDate,
	onDateSelect,
	minDate,
	maxDate,
	hideNonClickableRows = false,
	hideCalandar
}) => {
	const theme = useTheme();

	// Convert each show's date string → Date
	const showDates = useMemo(
		() => shows.map((s) => new Date(s.date)),
		[shows]
	);

	// Initial date = selectedDate or minDate or “today”
	const initialDate = selectedDate ?? minDate ?? new Date();

	// The “displayed” month/year
	const [displayedYear, setDisplayedYear] = useState(initialDate.getFullYear());
	const [displayedMonth, setDisplayedMonth] = useState(initialDate.getMonth());

	// For the Slide animation (left = prev, right = next)
	const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
	const [slideKey, setSlideKey] = useState(0);

	// Helper: is date in the show array?
	function isShowDate(date: Date) {
		return showDates.some((sd) => isSameDay(sd, date));
	}

	// Helper: is date within [minDate, maxDate]?
	function isWithinRange(date: Date) {
		if (minDate && date < minDate) return false;
		if (maxDate && date > maxDate) return false;
		return true;
	}

	// Generate the 42 days for the displayed month
	const calendarDays: Date[] = useMemo(() => {
		const startOfCurMonth = startOfMonth(new Date(displayedYear, displayedMonth));
		const endOfCurMonth = endOfMonth(startOfCurMonth);

		const start = startOfWeek(startOfCurMonth, { weekStartsOn: 0 });
		const end = endOfWeek(endOfCurMonth, { weekStartsOn: 0 });

		let tempDay = start;
		const days: Date[] = [];
		while (tempDay <= end) {
			days.push(tempDay);
			tempDay = addDays(tempDay, 1);
		}
		return days;
	}, [displayedYear, displayedMonth]);

	// Break calendarDays into weeks of 7
	const weeks: Date[][] = [];
	for (let i = 0; i < calendarDays.length; i += 7) {
		weeks.push(calendarDays.slice(i, i + 7));
	}

	// If hideNonClickableRows, filter out any week that does NOT have a clickable day
	const displayedWeeks = useMemo(() => {
		if (!hideNonClickableRows) {
			return weeks;
		}
		return weeks.filter((week) =>
			week.some((day) => isShowDate(day) && isWithinRange(day))
		);
	}, [weeks, hideNonClickableRows]);

	// Navigate previous month
	function handlePrevMonth() {
		setSlideDirection('left');
		let newYear = displayedYear;
		let newMonth = displayedMonth - 1;
		if (newMonth < 0) {
			newMonth = 11;
			newYear -= 1;
		}
		// If that’s < minDate’s month, block it
		const testDate = new Date(newYear, newMonth, 1);
		if (minDate && testDate < startOfMonth(minDate)) {
			return;
		}
		setDisplayedYear(newYear);
		setDisplayedMonth(newMonth);
		setSlideKey((k) => k + 1);
	}

	// Navigate next month
	function handleNextMonth() {
		setSlideDirection('right');
		let newYear = displayedYear;
		let newMonth = displayedMonth + 1;
		if (newMonth > 11) {
			newMonth = 0;
			newYear += 1;
		}
		// If that’s > maxDate’s month, block it
		const testDate = new Date(newYear, newMonth, 1);
		if (maxDate && testDate > startOfMonth(maxDate)) {
			return;
		}
		setDisplayedYear(newYear);
		setDisplayedMonth(newMonth);
		setSlideKey((k) => k + 1);
	}

	// When user clicks a day
	function handleDaySelect(date: Date) {
		if (onDateSelect && isShowDate(date) && isWithinRange(date)) {
			onDateSelect(date);
		}
	}

	const monthName = hebrewMonths[displayedMonth];
	const headerText = `${monthName} ${displayedYear}`;

	// Render row of weekday letters (א ב ג ד ה ו ש)
	function renderWeekdayHeader() {
		return (
			<Box display="flex" flexDirection="row">
				{hebrewWeekdays.map((letter) => (
					<Box
						key={letter}
						flex={1}
						textAlign="center"
						fontWeight="bold"
						fontSize={theme.typography.caption.fontSize}
					>
						{letter}
					</Box>
				))}
			</Box>
		);
	}

	// Render the final “displayedWeeks”
	function renderCalendarGrid() {
		return displayedWeeks.map((week, idx) => (
			<Box
				key={idx}
				display="flex"
				justifyContent="space-between"
				marginBottom={0.5}
			>
				{week.map((day) => {
					const isDisabled = !isShowDate(day) || !isWithinRange(day);
					const isSelected = selectedDate && isSameDay(day, selectedDate);

					return (
						<Box
							key={day.toISOString()}
							width={"2.2rem"}
							height="2.2rem"
							borderRadius="50%"
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontSize="0.8rem"
							sx={{
								cursor: isDisabled ? 'default' : 'pointer',
								border: `0.1px solid ${isDisabled ? "transparent" : theme.palette.primary.light}`,
								backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
								color: isSelected
									? '#fff'
									: isDisabled
										? 'rgba(0,0,0,0.38)'
										: 'inherit',
								'&:hover': {
									backgroundColor:
										!isDisabled && !isSelected
											? 'rgba(0, 0, 0, 0.08)'
											: undefined,
								},
							}}
							onClick={() => handleDaySelect(day)}
						>
							{day.getDate()}
						</Box>
					);
				})}
			</Box>
		));
	}

	return (
		<Box width={300} marginTop={3} overflow="hidden" dir="rtl" >
			{/* Header */}
			<Box
				display="flex"
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					// backgroundColor: 'rgba(0,0,0,0.1)',
					border: '1px solid rgba(0,0,0,0.17)',
					height: 40,
					px: 1,
					borderRadius: '0.6rem 0.6rem 0 0',

				}}
			>
				{/* Prev Month */}
				<IconButton
					onClick={handlePrevMonth}
					size="small"
					disabled={
						minDate &&
						startOfMonth(minDate) >= startOfMonth(
							new Date(displayedYear, displayedMonth)
						)
					}
					sx={{ '&:hover': { opacity: 0.8 } }}
				>
					{/* Right arrow for “previous” in RTL */}
					<ChevronRight />
				</IconButton>

				{/* Month & Year */}
				<Typography variant="body1" sx={{ cursor: 'default' }}>{headerText}</Typography>

				{/* Next Month */}
				<IconButton
					onClick={handleNextMonth}
					size="small"
					disabled={
						maxDate &&
						startOfMonth(maxDate) <= startOfMonth(
							new Date(displayedYear, displayedMonth)
						)
					}
					sx={{ '&:hover': { opacity: 0.8 } }}
				>
					{/* Left arrow for “next” in RTL */}
					<ChevronLeft />
				</IconButton>
			</Box>

			<Box sx={{
				border: `1px solid rgba(0,0,0,0.17)`,
				borderTop: 'none',
				borderRadius: '0 0 22px 22px',
			}} >
				{/* Weekday header */}
				<Box pt={1} pb={1}>
					{renderWeekdayHeader()}
				</Box>

				{/* Slide transition */}
				<Slide
					in
					direction={slideDirection}
					appear
					key={slideKey}
					timeout={300}
				>
					<Box pr={0.5} pl={0.5}>
						{renderCalendarGrid()}
					</Box>
				</Slide>
			</Box>
			<Button onClick={hideCalandar} variant="text" sx={{  borderRadius: '6rem', border: '1px solid rgba(0,0,0,0.17)', padding: '4px', marginTop: '4px' }}>
				סגירה
			</Button>
		</Box>
	);
};
